import lod_ from "lodash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	Tooltip,
	Box,
	CircularProgress
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import TextAreaScrollable from "components/Custom/TextAreaScrollable";
import KnowledgeActions from "redux-react/actions/knowledgeActions";
import { display } from "redux-react/reducers/snackBarReducer";
import { t } from "i18next";

/**
 * SplitKnowledge component
 * Allows splitting a knowledge into two parts: original content and new content.
 * @param {Object} props - Component properties
 * @param {Object} props.knowledge - The knowledge to split.
 * @param {Function} props.reload - Function to reload the knowledge after splitting.
 * @returns {JSX.Element} The rendered component.
 */
const SplitKnowledge = ({ knowledge, reload = () => {} }) => {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);

	const [content, setContent] = useState(knowledge?.content || "");
	const [rightContent, setRightContent] = useState("");
	const [openDialog, setOpenDialog] = useState(false);
	const [leftSelection, setLeftSelection] = useState({ start: 0, end: 0 });
	const [rightSelection, setRightSelection] = useState({ start: 0, end: 0 });

	/**
	 * Handle left selection change
	 * @param {Event} event - The selection change event
	 * @returns {void}
	 */
	const handleLeftSelectionChange = event => {
		const { selectionStart, selectionEnd } = event.target;
		setLeftSelection({ start: selectionStart, end: selectionEnd });
	};

	/**
	 * Handle right selection change
	 * @param {*} event - The selection change event
	 * @returns {void}
	 */
	const handleRightSelectionChange = event => {
		const { selectionStart, selectionEnd } = event.target;
		setRightSelection({ start: selectionStart, end: selectionEnd });
	};

	/**
	 * Move selected text from left textarea to right textarea
	 * @returns {void}
	 */
	const moveSelectedTextToRight = () => {
		const selectedText = content.substring(leftSelection.start, leftSelection.end);
		if (selectedText) {
			setRightContent(prev => prev + (prev ? "\n\n" : "") + selectedText);
			const newContent =
				content.substring(0, leftSelection.start) + content.substring(leftSelection.end);
			setContent(newContent);
			setLeftSelection({ start: 0, end: 0 }); // Reset selection after moving
		}
	};

	/**
	 * Move selected text from right textarea to left textarea
	 * @returns {void}
	 */
	const moveSelectedTextToLeft = () => {
		const selectedText = rightContent.substring(rightSelection.start, rightSelection.end);
		if (selectedText) {
			setContent(prev => prev + (prev ? "\n\n" : "") + selectedText);
			const newRightContent =
				rightContent.substring(0, rightSelection.start) +
				rightContent.substring(rightSelection.end);
			setRightContent(newRightContent);
			setRightSelection({ start: 0, end: 0 }); // Reset selection after moving
		}
	};

	/**
	 * Check if the save button should be disabled
	 * @returns {boolean} True if the save button should be disabled, false otherwise
	 */
	const isDisabled = () => {
		if (loading) {
			return true;
		}
		if (lod_.isNil(rightContent) || rightContent.trim() === "") {
			return true;
		}
		if (lod_.isNil(content) || content.trim() === "") {
			return true;
		}
		if (lod_.isEqual(content, rightContent)) {
			return true;
		}
		return false;
	};

	/**
	 * Handle save action
	 * This function creates a new knowledge with the right content and updates the original knowledge
	 * with the left content.
	 * @returns {void}
	 */
	const onSave = () => {
		if (lod_.isNil(knowledge.uuid)) {
			return;
		}

		setLoading(true);

		const displayError = () => {
			setLoading(false);
			dispatch(
				display({
					type: "error",
					message: t("FAIQ.ERROR.errorWhileUpdateKnowledge")
				})
			);
		};

		const onSuccess = () => {
			dispatch(
				display({
					type: "success",
					message: t("FAIQ.SUCCESS.knowledgeUpdated")
				})
			);
			setLoading(false);
			setOpenDialog(false);
			reload();
		};

		const updateOriginalKnowledge = () => {
			dispatch(
				KnowledgeActions.updateKnowledge(
					knowledge.uuid,
					{ content },
					() => onSuccess(),
					() => displayError()
				)
			);
		};

		// 1- Create the new knowledge with the right content
		// 2- Update the original knowledge with the left content
		dispatch(
			KnowledgeActions.createKnowledge(
				{ content: rightContent, metadatas: knowledge.metadatas || {} },
				() => {
					updateOriginalKnowledge();
				},
				() => displayError()
			)
		);
	};

	useEffect(() => {
		setContent(knowledge?.content || "");
		setRightContent("");
		setLeftSelection({ start: 0, end: 0 });
		setRightSelection({ start: 0, end: 0 });
	}, [knowledge]);

	return (
		<MDBox>
			{/* Button */}
			<Tooltip title={t("SETTINGS.split")} placement="top">
				<IconButton size="small" onClick={() => setOpenDialog(true)}>
					<Icon>call_split</Icon>
				</IconButton>
			</Tooltip>
			{/* Dialog */}
			<Dialog
				fullWidth
				open={openDialog}
				PaperProps={{
					sx: {
						height: "95%",
						width: "100%",
						maxWidth: "100%"
					}
				}}
			>
				<DialogTitle>{t("FAIQ.ACTIONS.splitContent")}</DialogTitle>
				<DialogContent
					sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
				>
					<Box sx={{ display: "flex", gap: 2, height: "100%", mt: 1 }}>
						{/* Left textarea */}
						<Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
							<TextAreaScrollable
								value={content}
								onChange={e => setContent(e.target.value)}
								label={t("FAIQ.ACTIONS.originalContentLabel")}
								placeholder={t("FAIQ.ACTIONS.originalContentPlaceholder")}
								onSelect={handleLeftSelectionChange}
								InputLabelProps={{ shrink: true }}
								disabled={loading}
							/>
						</Box>

						{/* Transfer buttons */}
						<Box
							sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}
						>
							<IconButton
								size="small"
								onClick={moveSelectedTextToRight}
								disabled={leftSelection.start === leftSelection.end}
							>
								<Icon>arrow_forward</Icon>
							</IconButton>
							<IconButton
								size="small"
								onClick={moveSelectedTextToLeft}
								disabled={rightSelection.start === rightSelection.end}
							>
								<Icon>arrow_back</Icon>
							</IconButton>
						</Box>

						{/* Right textarea */}
						<Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
							<TextAreaScrollable
								value={rightContent}
								onChange={e => setRightContent(e.target.value)}
								placeholder={t("FAIQ.ACTIONS.writeNewContentPlaceholder")}
								onSelect={handleRightSelectionChange}
								InputLabelProps={{ shrink: true }}
								label={t("FAIQ.ACTIONS.writeNewContentLabel")}
								disabled={loading}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<MDButton
						variant="outlined"
						color="info"
						onClick={() => setOpenDialog(false)}
						disabled={loading}
					>
						{t("SETTINGS.cancel")}
					</MDButton>
					{!loading && (
						<MDButton variant="contained" color="info" onClick={onSave} disabled={isDisabled()}>
							{t("SETTINGS.save")}
						</MDButton>
					)}
					{loading && (
						<MDButton variant="contained" color="info" disabled>
							<CircularProgress size={24} color="white" />
							&nbsp;{t("SETTINGS.saving")}
						</MDButton>
					)}
				</DialogActions>
			</Dialog>
		</MDBox>
	);
};

export default SplitKnowledge;
